import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { type Column, DataTable } from "@/components/admin/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAdminDiscounts } from "@/hooks/useAdminData";
import type { Discount } from "@/lib/api-types";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function AdminDiscountList() {
	const { t } = useTranslation();
	const { data, isLoading } = useAdminDiscounts();

	const discounts = data?.discounts ?? [];

	const columns: Column<Discount & Record<string, unknown>>[] = [
		{
			key: "code",
			header: t("admin.code"),
			sortable: true,
			render: (item) => (
				<Link
					to={`/admin/discounts/${item.id}`}
					className="font-mono font-medium text-accent hover:underline"
				>
					{item.code}
				</Link>
			),
		},
		{
			key: "type",
			header: t("admin.type"),
			render: (item) => (
				<Badge variant="outline" className="capitalize">
					{item.type}
				</Badge>
			),
		},
		{
			key: "amount",
			header: t("admin.amount"),
			sortable: true,
			render: (item) => (
				<span className="text-primary">
					{item.type === "percentage"
						? `${item.percent_off}%`
						: `$${((item.amount_off ?? 0) / 100).toFixed(2)}`}
				</span>
			),
		},
		{
			key: "times_redeemed",
			header: t("admin.uses"),
			render: (item) => (
				<span className="text-secondary">
					{item.times_redeemed}
					{(item.max_redemptions as number) > 0 && ` / ${item.max_redemptions}`}
				</span>
			),
		},
		{
			key: "active",
			header: t("admin.status"),
			render: (item) => (
				<Badge
					className={
						item.active
							? "border-transparent bg-success-muted text-success"
							: "border-transparent bg-error-muted text-error"
					}
				>
					{item.active ? t("admin.active") : t("admin.inactive")}
				</Badge>
			),
		},
		{
			key: "expires_at",
			header: t("admin.expiryDate"),
			sortable: true,
			render: (item) => (
				<span className="text-secondary">
					{item.expires_at
						? new Date((item.expires_at as number) * 1000).toLocaleDateString()
						: "-"}
				</span>
			),
		},
	];

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[{ label: t("admin.dashboard"), href: "/admin" }, { label: t("admin.discounts") }]}
			/>

			<div className="flex items-center justify-between">
				<h1 className="text-3xl font-bold text-primary">{t("admin.discounts")}</h1>
				<Button asChild>
					<Link to="/admin/discounts/new">
						<Plus className="mr-2 h-4 w-4" />
						{t("admin.newDiscount")}
					</Link>
				</Button>
			</div>

			<DataTable
				columns={columns}
				data={discounts as (Discount & Record<string, unknown>)[]}
				isLoading={isLoading}
				emptyMessage={t("admin.noDiscounts")}
			/>
		</div>
	);
}
